import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import { ThemeContext } from "./contexts/ThemeContext";
import { NavbarContext } from "./contexts/NavbarContext";
import { BLOG, CONTACT, RDV, REALISATIONS, SERVICES, blogArticle } from "../config/routes";

import "./styles/ArticleGenericPage.css";

function stripHtml(html = "") {
  return String(html).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function excerptFromHtml(html = "", maxLen = 160) {
  const text = stripHtml(html);
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen - 1).trim() + "…";
}

export default function ArticleGenericPage() {
  const { articleId } = useParams();
  const navigate = useNavigate();

  const { darkMode } = useContext(ThemeContext);
  const { navbarActive, setNavbarActive, scrollPosition } = useContext(NavbarContext);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 599);

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState(null);
  const [content, setContent] = useState("");
  const [headerImage, setHeaderImage] = useState("");
  const [publishedAt, setPublishedAt] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 599);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) {
          setErrorMsg("REACT_APP_API_URL manquant. Impossible de charger l’article.");
          setLoading(false);
          return;
        }

        const response = await axios.get(`${apiUrl}/article/${articleId}`);
        const data = response.data;

        setTitle(data.title || "");
        setAuthor({
          name: data.name || "",
          first_name: data.first_name || "",
          picture: data.picture || "",
          bio: data.bio || "",
          instagram_url: data.instagram_url || "",
        });
        setContent(data.content || "");
        setHeaderImage(data.header_image || "");

        setPublishedAt(data.published_at || data.date || "");
        setCategory(data.category || "");
      } catch (error) {
        if (error?.response) {
          const statusCode = error.response.status;
          if (statusCode === 404) return navigate("/404");
          if (statusCode === 500) return navigate("/500");
        }
        setErrorMsg("Erreur lors du chargement de l’article.");
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [articleId, navigate]);

  useEffect(() => {
    const preventTouchMove = (e) => e.preventDefault();
    if (navbarActive && isMobile) {
      document.body.addEventListener("touchmove", preventTouchMove, { passive: false });
    } else {
      document.body.removeEventListener("touchmove", preventTouchMove);
    }
    return () => document.body.removeEventListener("touchmove", preventTouchMove);
  }, [navbarActive, isMobile]);

  useEffect(() => {
    const mainContainer = document.querySelector(".article2026");
    if (!mainContainer) return;
    if (navbarActive) {
      mainContainer.classList.add("menu-open");
      document.body.classList.add("menu-open");
    } else {
      mainContainer.classList.remove("menu-open");
      document.body.classList.remove("menu-open");
    }
  }, [navbarActive]);

  useEffect(() => {
    if (!navbarActive) window.scrollTo(0, scrollPosition);
  }, [navbarActive, scrollPosition]);

  const handleMainContainerClick = () => {
    if (navbarActive && isMobile) setNavbarActive(false);
  };

  const safeTitle = useMemo(() => stripHtml(title), [title]);
  const description = useMemo(() => excerptFromHtml(content, 170), [content]);

  const canonicalUrl = useMemo(
    () => `https://dreameragency.fr${blogArticle(articleId)}`,
    [articleId]
  );

  const authorName = useMemo(() => {
    if (!author) return "Dreamer Agency";
    const full = `${author.first_name || ""} ${author.name || ""}`.trim();
    return full || "Dreamer Agency";
  }, [author]);

  const jsonLd = useMemo(() => {
    const obj = {
      "@context": "https://schema.org",
      "@type": "Article",
      mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
      headline: safeTitle || "Article",
      description,
      image: headerImage ? [headerImage] : undefined,
      author: { "@type": "Person", name: authorName },
      publisher: { "@type": "Organization", name: "Dreamer Agency" },
      datePublished: publishedAt || undefined,
      dateModified: publishedAt || undefined,
    };
    return [obj];
  }, [canonicalUrl, safeTitle, description, headerImage, authorName, publishedAt]);

  if (loading) {
    return (
      <main className={`article2026 ${darkMode ? "dark" : "light"}`} onClick={handleMainContainerClick}>
        <div className="article2026-container article2026-loading">Chargement…</div>
      </main>
    );
  }

  if (errorMsg) {
    return (
      <main className={`article2026 ${darkMode ? "dark" : "light"}`} onClick={handleMainContainerClick}>
        <div className="article2026-container">
          <div className="article2026-error">{errorMsg}</div>
          <div className="article2026-actions">
            <Link to={BLOG} className="btn btn-secondary">Retour blog</Link>
            <Link to={RDV} className="btn btn-primary">Réserver un appel</Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className={`article2026 ${darkMode ? "dark" : "light"}`} onClick={handleMainContainerClick}>
      <Helmet>
        <title>{safeTitle ? `${safeTitle} - Dreamer Agency` : "Article - Dreamer Agency"}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:title" content={safeTitle ? `${safeTitle} - Dreamer Agency` : "Article - Dreamer Agency"} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={headerImage || "https://dreameragency.fr/media/OG/blog_background.png"} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={safeTitle || "Article - Dreamer Agency"} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={headerImage || "https://dreameragency.fr/media/OG/blog_background.png"} />
      </Helmet>

      <header className="article2026-hero">
        <div className="article2026-heroBg" style={headerImage ? { backgroundImage: `url(${headerImage})` } : undefined} />
        <div className="article2026-heroOverlay" />

        <div className="article2026-container article2026-heroInner">
          <nav className="article2026-breadcrumb" aria-label="Breadcrumb">
            <Link to="/" className="crumb">Accueil</Link>
            <span className="sep">/</span>
            <Link to={BLOG} className="crumb">Insights</Link>
            <span className="sep">/</span>
            <span className="crumbCurrent" aria-current="page">{safeTitle || "Article"}</span>
          </nav>

          <div className="article2026-heroMeta">
            {category ? <span className="metaTag">{category}</span> : null}
            {publishedAt ? <span className="metaMuted">{publishedAt}</span> : null}
            <span className="metaMuted">•</span>
            <span className="metaMuted">{authorName}</span>
          </div>

          <h1 className="article2026-h1" dangerouslySetInnerHTML={{ __html: title }} />

          <p className="article2026-lead">{description}</p>

          <div className="article2026-heroActions">
            <Link to={RDV} className="btn btn-primary">Réserver un appel</Link>
            <Link to={SERVICES} className="btn btn-secondary">Voir les services</Link>
            <Link to={REALISATIONS} className="btn btn-ghost">Réalisations</Link>
          </div>
        </div>
      </header>

      <section className="article2026-section">
        <div className="article2026-container article2026-grid">
          <article className="article2026-content">
            <div className="article2026-prose" dangerouslySetInnerHTML={{ __html: content }} />

            <div className="article2026-endCta">
              <div>
                <h2 className="article2026-h2">Tu veux appliquer ça à ton projet ?</h2>
                <p className="article2026-muted">
                  Réserve un appel : on clarifie le scope MVP, les priorités, et une estimation réaliste.
                </p>
              </div>
              <div className="article2026-endActions">
                <Link to={RDV} className="btn btn-primary">Réserver un appel</Link>
                <Link to={CONTACT} className="btn btn-ghost">Contact</Link>
              </div>
            </div>
          </article>

          <aside className="article2026-side" aria-label="Sidebar">
            <div className="sideCard">
              <p className="sideTitle">Aller vite</p>
              <p className="article2026-muted">
                Le blog donne les idées. Nous, on exécute : Web / SaaS / Mobile / Growth.
              </p>
              <Link to={RDV} className="btn btn-primary btn-block">Réserver un appel</Link>
              <Link to={SERVICES} className="btn btn-secondary btn-block">Voir les services</Link>
            </div>

            {author ? (
              <div className="sideCard sideCardSoft">
                <p className="sideTitle">Auteur</p>
                <div className="authorRow">
                  {author.picture ? (
                    <img src={author.picture} alt={authorName} className="authorAvatar" />
                  ) : (
                    <div className="authorAvatarPlaceholder" aria-hidden="true">
                      <span>{authorName.slice(0, 1).toUpperCase()}</span>
                    </div>
                  )}
                  <div>
                    <div className="authorName">{authorName}</div>
                    {author.instagram_url ? (
                      <a className="authorLink" href={author.instagram_url} target="_blank" rel="noreferrer">
                        Instagram →
                      </a>
                    ) : null}
                  </div>
                </div>
                {author.bio ? <p className="article2026-muted">{author.bio}</p> : null}
              </div>
            ) : null}

            <div className="sideCard sideCardSoft">
              <p className="sideTitle">Ressources</p>
              <div className="sideLinks">
                <Link to={REALISATIONS} className="sideLink">Réalisations</Link>
                <Link to={SERVICES} className="sideLink">Services</Link>
                <Link to={CONTACT} className="sideLink">Contact</Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {jsonLd.map((obj, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </main>
  );
}
